import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import {
  ArrowRight,
  CheckCircle2,
  Phone,
  Upload,
  TrendingUp,
  Building,
  FileText,
  Download
} from 'lucide-react';

export const CountryTemplate = ({ countryData }) => {
  const {
    name,
    region,
    metaTitle,
    metaDescription,
    hero,
    atAGlance,
    marketOverview,
    diseaseburden,
    regulatoryFramework,
    pharmaPathway,
    nutraPathway,
    requiredDocs,
    whyThisCountry,
    howWeHelp,
    faqs,
    color
  } = countryData;

  const [activeTab, setActiveTab] = useState('pharma');

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
      </Helmet>

      {/* Hero Section - Diagonal Split */}
      <section className="relative pt-32 pb-32 px-6 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${color}08 0%, transparent 100%)`,
            clipPath: 'polygon(0 0, 100% 0, 100% 85%, 0 100%)'
          }}
        />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <Badge className="px-6 py-2 text-sm" style={{ backgroundColor: color, color: 'white' }}>
              {region}
            </Badge>
            <div className="h-px flex-1 max-w-24" style={{ backgroundColor: color + '40' }} />
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight" style={{ color: '#303040' }}>
            {hero.title}
          </h1>
          <p className="text-2xl mb-12 leading-relaxed max-w-4xl" style={{ color: '#708080' }}>
            {hero.subtitle}
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/contact">
              <Button
                size="lg"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: color, color: 'white' }}
              >
                {hero.cta}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            {hero.secondaryCta && (
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ borderColor: color, color: color }}
              >
                {hero.secondaryCta}
                <Download className="ml-2 h-5 w-5" />
              </Button>
            )}
            <a
              href="#country-faqs"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('country-faqs')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <Button
                size="lg"
                variant="ghost"
                className="text-lg px-6 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ color: color }}
              >
                View FAQs
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* At A Glance - Flowing Grid */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-bold mb-16 text-center" style={{ color: '#303040' }}>
            {name} at a Glance
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            {atAGlance.map((stat, index) => (
              <div key={index} className="relative group">
                <div className="absolute -inset-1 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ backgroundColor: color + '20' }} />
                <div className="relative p-8 rounded-xl bg-white">
                  <div className="text-sm font-semibold mb-3 uppercase tracking-wide" style={{ color: color }}>
                    {stat.label}
                  </div>
                  <div className="text-4xl font-bold" style={{ color: '#303040' }}>
                    {stat.value}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {atAGlance.insight && (
            <div className="mt-16 relative">
              <div className="absolute left-0 top-0 bottom-0 w-1 rounded-full" style={{ backgroundColor: color }} />
              <div className="pl-8">
                <div className="flex items-start gap-4">
                  <TrendingUp className="h-6 w-6 mt-1 flex-shrink-0" style={{ color: color }} />
                  <p className="text-xl leading-relaxed" style={{ color: '#708080' }}>
                    <strong style={{ color: color }}>Key Insight: </strong>
                    {atAGlance.insight}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Market Overview - Split Layout */}
      <section className="py-24 px-6" style={{ backgroundColor: color + '05' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl font-bold mb-8" style={{ color: '#303040' }}>
                {marketOverview.title}
              </h2>
              <div className="space-y-6">
                {marketOverview.stats.map((stat, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full mt-3 flex-shrink-0" style={{ backgroundColor: color }} />
                    <p className="text-xl leading-relaxed" style={{ color: '#708080' }}>{stat}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="relative">
                <div className="absolute -inset-4 rounded-3xl opacity-20" style={{ backgroundColor: color }} />
                <div className="relative bg-white rounded-2xl p-10 shadow-xl">
                  <h3 className="text-2xl font-bold mb-6" style={{ color: '#303040' }}>Key Opportunities</h3>
                  <div className="space-y-4">
                    {marketOverview.opportunities.map((opp, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="h-6 w-6 mt-0.5 flex-shrink-0" style={{ color: color }} />
                        <span className="text-lg" style={{ color: '#708080' }}>{opp}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disease Burden - Horizontal Flow */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6" style={{ color: '#303040' }}>
              {diseaseburden.title}
            </h2>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: '#708080' }}>
              {diseaseburden.subtitle}
            </p>
          </div>
          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5" style={{ backgroundColor: color + '20' }} />
            
            <div className="grid md:grid-cols-3 gap-8 relative">
              {diseaseburden.categories.map((category, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300">
                    <div className="p-6 text-center" style={{ backgroundColor: color }}>
                      <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                        <span className="text-3xl font-bold text-white">{index + 1}</span>
                      </div>
                      <h3 className="text-2xl font-bold text-white">{category.name}</h3>
                    </div>
                    <div className="p-6">
                      <ul className="space-y-3">
                        {category.items.map((item, idx) => (
                          <li key={idx} className="text-base leading-relaxed" style={{ color: '#708080' }}>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Regulatory Framework - Authority Badge */}
      <section className="py-24 px-6" style={{ backgroundColor: color + '05' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-bold mb-16 text-center" style={{ color: '#303040' }}>
            Regulatory Framework
          </h2>
          
          {/* Authority */}
          <div className="mb-16">
            <div className="relative inline-block">
              <div className="absolute -inset-4 rounded-2xl" style={{ backgroundColor: color + '15' }} />
              <div className="relative flex items-center gap-4 p-8 bg-white rounded-xl">
                <Building className="h-12 w-12 flex-shrink-0" style={{ color: color }} />
                <div>
                  <h3 className="text-2xl font-bold mb-2" style={{ color: '#303040' }}>
                    {regulatoryFramework.authority}
                  </h3>
                  <p className="text-lg" style={{ color: '#708080' }}>
                    {regulatoryFramework.description}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Pharma vs Nutra - Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-2 mb-12 h-auto p-2 rounded-2xl" style={{ backgroundColor: color + '15' }}>
              <TabsTrigger
                value="pharma"
                className="data-[state=active]:shadow-lg rounded-xl py-4 text-lg transition-all duration-300"
                style={{
                  backgroundColor: activeTab === 'pharma' ? 'white' : 'transparent',
                  color: activeTab === 'pharma' ? color : '#708080'
                }}
              >
                Pharmaceutical Products
              </TabsTrigger>
              <TabsTrigger
                value="nutra"
                className="data-[state=active]:shadow-lg rounded-xl py-4 text-lg transition-all duration-300"
                style={{
                  backgroundColor: activeTab === 'nutra' ? 'white' : 'transparent',
                  color: activeTab === 'nutra' ? color : '#708080'
                }}
              >
                Nutraceuticals
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pharma">
              <div className="bg-white rounded-2xl p-10 shadow-lg">
                <div className="mb-8">
                  <div className="inline-block px-6 py-2 rounded-full mb-4" style={{ backgroundColor: color + '15' }}>
                    <span className="font-bold" style={{ color: color }}>Timeline: {regulatoryFramework.pharma.timeline}</span>
                  </div>
                </div>
                <h4 className="text-xl font-bold mb-6" style={{ color: '#303040' }}>Key Requirements</h4>
                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  {regulatoryFramework.pharma.requirements.map((req, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-4 rounded-xl hover:shadow-md transition-shadow" style={{ backgroundColor: color + '05' }}>
                      <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: color }} />
                      <span style={{ color: '#708080' }}>{req}</span>
                    </div>
                  ))}
                </div>
                {regulatoryFramework.pharma.insight && (
                  <div className="p-6 rounded-xl" style={{ backgroundColor: color + '10', borderLeft: `4px solid ${color}` }}>
                    <p className="italic" style={{ color: '#708080' }}>
                      <strong style={{ color: color }}>Insight: </strong>
                      {regulatoryFramework.pharma.insight}
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="nutra">
              <div className="bg-white rounded-2xl p-10 shadow-lg">
                <div className="mb-8">
                  <div className="inline-block px-6 py-2 rounded-full mb-4" style={{ backgroundColor: color + '15' }}>
                    <span className="font-bold" style={{ color: color }}>Timeline: {regulatoryFramework.nutra.timeline}</span>
                  </div>
                </div>
                <h4 className="text-xl font-bold mb-6" style={{ color: '#303040' }}>Key Requirements</h4>
                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  {regulatoryFramework.nutra.requirements.map((req, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-4 rounded-xl hover:shadow-md transition-shadow" style={{ backgroundColor: color + '05' }}>
                      <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: color }} />
                      <span style={{ color: '#708080' }}>{req}</span>
                    </div>
                  ))}
                </div>
                {regulatoryFramework.nutra.insight && (
                  <div className="p-6 rounded-xl" style={{ backgroundColor: color + '10', borderLeft: `4px solid ${color}` }}>
                    <p className="italic" style={{ color: '#708080' }}>
                      <strong style={{ color: color }}>Insight: </strong>
                      {regulatoryFramework.nutra.insight}
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Registration Pathways - Timeline Style */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-bold mb-16 text-center" style={{ color: '#303040' }}>
            Registration Pathways
          </h2>
          <div className="grid md:grid-cols-2 gap-16">
            {[
              { data: pharmaPathway, side: 'left' },
              { data: nutraPathway, side: 'right' }
            ].map(({ data, side }, pIndex) => (
              <div key={pIndex}>
                <h3 className="text-3xl font-bold mb-8" style={{ color: '#303040' }}>
                  {data.title}
                </h3>
                <div className="relative space-y-6">
                  {/* Vertical Line */}
                  <div className="absolute left-6 top-8 bottom-8 w-0.5" style={{ backgroundColor: color + '30' }} />
                  
                  {data.steps.map((step, index) => (
                    <div key={index} className="relative flex items-start gap-6">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 font-bold text-white shadow-lg"
                        style={{ backgroundColor: color }}
                      >
                        {index + 1}
                      </div>
                      <div className="flex-1 pt-2">
                        <p className="text-lg" style={{ color: '#708080' }}>{step}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Required Documents - Alternating Layout */}
      <section className="py-24 px-6" style={{ backgroundColor: color + '05' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-bold mb-16 text-center" style={{ color: '#303040' }}>
            Required Documents
          </h2>
          <div className="space-y-12">
            {Object.entries(requiredDocs).map(([category, docs], index) => (
              <div key={category} className={`flex ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'} items-center gap-12`}>
                <div className="flex-1">
                  <div className="inline-block px-8 py-4 rounded-full mb-6" style={{ backgroundColor: color }}>
                    <h3 className="text-2xl font-bold text-white">{category}</h3>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="space-y-3">
                    {docs.map((doc, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-xl hover:shadow-md transition-shadow">
                        <FileText className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: color }} />
                        <span style={{ color: '#708080' }}>{doc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why This Country - Checkmark Flow */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-bold mb-16 text-center" style={{ color: '#303040' }}>
            {whyThisCountry.title}
          </h2>
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {whyThisCountry.reasons.map((reason, index) => (
              <div key={index} className="flex items-start gap-4 p-6 bg-white rounded-xl hover:shadow-lg transition-shadow">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color }}>
                  <CheckCircle2 className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-xl pt-0.5" style={{ color: '#303040' }}>{reason}</h3>
              </div>
            ))}
          </div>
          {whyThisCountry.bestPortfolio && (
            <div className="relative mt-16">
              <div className="absolute inset-0 rounded-3xl opacity-10" style={{ backgroundColor: color }} />
              <div className="relative p-10 rounded-2xl">
                <h3 className="text-3xl font-bold mb-8 text-center" style={{ color: color }}>
                  Best Portfolio Approach
                </h3>
                <div className="grid md:grid-cols-3 gap-6">
                  {whyThisCountry.bestPortfolio.map((item, idx) => (
                    <div key={idx} className="text-center p-6 bg-white rounded-xl">
                      <ArrowRight className="h-8 w-8 mx-auto mb-4" style={{ color: color }} />
                      <p className="text-lg" style={{ color: '#708080' }}>{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* How We Help - Simple List */}
      <section className="py-24 px-6" style={{ backgroundColor: color + '05' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-5xl font-bold mb-16 text-center" style={{ color: '#303040' }}>
            {howWeHelp.title}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {howWeHelp.services.map((service, index) => (
              <div key={index} className="flex items-center gap-4 p-6 bg-white rounded-xl">
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                <span className="text-lg" style={{ color: '#708080' }}>{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs - Accordion */}
      <section id="country-faqs" className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-5xl font-bold mb-16 text-center" style={{ color: '#303040' }}>
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`faq-${index}`}
                className="border-0 rounded-2xl overflow-hidden bg-white shadow-md"
              >
                <AccordionTrigger className="px-8 py-6 hover:no-underline text-left">
                  <span className="text-lg font-bold pr-8" style={{ color: '#303040' }}>
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-8 pb-6">
                  <p className="text-lg leading-relaxed" style={{ color: '#708080' }}>{faq.answer}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA - Centered */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-5xl font-bold mb-6" style={{ color: '#303040' }}>
            Start Your {name} Registration With Clarity & Confidence
          </h2>
          <p className="text-xl mb-12" style={{ color: '#708080' }}>
            Share your product list and we'll create a submission-ready regulatory roadmap.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true" 
              target="_blank" 
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open('https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true', '_blank', 'noopener,noreferrer');
              }}
            >
              <Button
                size="lg"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: color, color: 'white' }}
              >
                Book a Call
                <Phone className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <Link to="/contact#send-us-your-details">
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ borderColor: color, color: color }}
              >
                Upload Product List
                <Upload className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
